<template>
  <article>
    <h1 class="text-h3" id="notes" v-intersect="onIntersect">Release Notes</h1>

    <h2 class="text-h4" id="v5-3" v-intersect="onIntersect">v5.3</h2>
    <p>March 2026</p>
    <h3>Additions</h3>
    <ul>
      <li>Added economics reports with data preview and configurable Excel export.</li>
      <li>Added Excel export functionality to Economics view.</li>
      <li>Added support for v5 models with UBJ technique for loading .model files from S3.</li>
      <li>Added delete action to ReportViewSet for bulk report deletion.</li>
      <li>Added user quotas and progress bars with data refresh on prediction.</li>
      <li>Added ability to randomly pick 10 random wells for testing.</li>
      <li>Added well import via shapefiles.</li>
      <li>Implemented DCA curve fitting for DUCs/Permits predictions.</li>
      <li>Added direction for when termination required on curve vs well.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Format Start Date dimension as 'Mon YYYY' in forecasting aggregates.</li>
      <li>Renamed ducs-permits to predict.</li>
      <li>Split up termination functionality for better control.</li>
      <li>Updated logging system.</li>
      <li>Removed one identifier.</li>
      <li>Updated connection and method fixtures.</li>
      <li>Special condition for handling .model files.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed #159 - user quotas implementation.</li>
      <li>Fixed #158 - well import via shapefiles.</li>
      <li>Fixed #161 - ratios bypassed when any missing targets.</li>
      <li>Fixed #157 - terminating wells and sync.</li>
      <li>Fixed missing gas or oil in outputs.</li>
      <li>Fixed oil/gas ratio calculation returning 600 months of zeros.</li>
      <li>Fixed fragmentation warning.</li>
      <li>Fixed single target ratios.</li>
      <li>Fixed incorrect filter.</li>
      <li>Fixed missing perfLen in agent.</li>
      <li>Fixed group params in exports.</li>
    </ul>

    <h2 class="text-h4" id="v5-2" v-intersect="onIntersect">v5.2</h2>
    <p>December 2025</p>
    <h3>Additions</h3>
    <ul>
      <li>Added group well count tracking and display functionality.</li>
      <li>Added single well mode for economics analysis.</li>
      <li>Added report generation with configurable options and templates.</li>
      <li>Added visual locking indicators for groups with color-coded status.</li>
      <li>Added individual well termination capabilities.</li>
      <li>Added toggle menu functionality for improved navigation.</li>
      <li>Added comprehensive quality assessment system with three-tier checks.</li>
      <li>Added well editing dialog with improved parameter controls.</li>
      <li>Added target color badges for visual distinction.</li>
      <li>Added technique authorization model for enhanced security control.</li>
      <li>Introduced welldb for improved well database management.</li>
      <li>Added django-import-export allowing admin to manage basin mapping.</li>
      <li>Added validation and explanations for multi-basin operations.</li>
      <li>Added file logging functionality.</li>
      <li>Writing basic tests for backend.</li>
      <li>Added columns to identifiers.</li>
      <li>Added Headers sheet to Excel report with well data from prediction filters.</li>
      <li>Added Assumptions sheet to Excel report with full template structure and expanded economic columns.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Completely redesigned and reorganized groups architecture.</li>
      <li>Moved groups to predictions model with enhanced data organization.</li>
      <li>Added synchronization for locked groups to prevent data conflicts.</li>
      <li>Improved quality implementation with new filtering system.</li>
      <li>Adjusted well and filter computation methods for better accuracy.</li>
      <li>Standardized start date handling across all forecasting operations.</li>
      <li>Adjusted frame filtering algorithm and capabilities.</li>
      <li>Enhanced export format with new data structure.</li>
      <li>Improved group layout and content presentation.</li>
      <li>Updated well termination logic with better decline handling.</li>
      <li>Moved economics configuration to group settings.</li>
      <li>Renamed forecasting to forecast.</li>
      <li>Updated PF to ignore basin in data.</li>
      <li>Updated application fixture.</li>
      <li>Replaced basin names with original in filters.</li>
      <li>Updated identifier base data.</li>
      <li>Updated factories for testing.</li>
      <li>Making sure the log dir exists.</li>
      <li>Adding additional basin mapping.</li>
      <li>Adjusting for basin names.</li>
      <li>Casting MAX as integer.</li>
      <li>Cleaned up betterfix remnants.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed errors when basin name is mismatched or missing.</li>
      <li>Fixed error saving well parameters.</li>
      <li>Fixed quality implementation for better accuracy and reliability.</li>
      <li>Fixed automatic curvefit logic for petroleum engineering standards.</li>
      <li>Fixed normalize and scale functions to handle non-numeric columns like Start Date.</li>
      <li>Fixed terminal decline to check current decline rate before applying termination.</li>
      <li>Fixed sudden cutoff in well termination calculations.</li>
      <li>Fixed extra month appearing in forecast outputs.</li>
      <li>Fixed quality filtering issues with well selection.</li>
      <li>Fixed year one calculations for single well analysis.</li>
      <li>Fixed ratio output to display zero when null instead of error.</li>
      <li>Fixed infinity values in calculation outputs.</li>
      <li>Fixed locking mechanism with different targets.</li>
      <li>Fixed incorrect padding for Arps parameters.</li>
      <li>Fixed decline rate rounding precision.</li>
      <li>Fixed single well loading issues.</li>
      <li>Fixed sync issues between groups and predictions.</li>
      <li>Fixed individual well termination calculations.</li>
      <li>Fixed missing dates for decline calculations.</li>
      <li>Fixed EUR calculations for single well mode.</li>
      <li>Fixed missing oil and gas volumes in output tables.</li>
      <li>Fixed technique selection and application issues.</li>
      <li>Fixed incorrect group IDs in database operations.</li>
      <li>Fixed missing group economics settings on startup.</li>
      <li>Fixed immediate economics rendering issues that caused display delays.</li>
      <li>Fixed #150 - incorrect single well display (reverted previous changes).</li>
      <li>Fixed #154 and potentially #150.</li>
      <li>Fixed issue with multi-basin production forecasting.</li>
      <li>Fixed incorrect multi-basin alignment.</li>
      <li>Fixed issues with PF compilation.</li>
      <li>Fixed TypeError in prepare_basin_list when joining non-string basin values.</li>
      <li>Fixed CurveFit errors.</li>
      <li>Fixed single well EUR calculations.</li>
      <li>Fixed default connections.</li>
      <li>Handling easttexas in prediction.</li>
      <li>Catching check_well errors.</li>
      <li>Fixed Wells_Monthly sheet generation - process full group data instead of individual wells.</li>
      <li>Fixed application slug.</li>
      <li>Fixed bad target name.</li>
    </ul>

    <h2 class="text-h4" id="v5-1" v-intersect="onIntersect">v5.1</h2>
    <p>October 2025</p>
    <h3>Additions</h3>
    <ul>
      <li>Added comprehensive in-app economics for type curves.</li>
      <li>Added quality tags on dataframes when predicting with filtering capabilities.</li>
      <li>Added type curve saving within groups for better organization.</li>
      <li>Added reverse terminal decline implementation.</li>
      <li>Added user session management with enhanced security.</li>
      <li>Added ability to select database identifiers with improved PostgreSQL support.</li>
      <li>Added LastWellStatus to available filters.</li>
      <li>Added configurable technique options with improved logging.</li>
      <li>Added progress bar for better user feedback during operations.</li>
      <li>Added melt and pivot functions for advanced data manipulation.</li>
      <li>Added ability to parse date-like strings for improved data import.</li>
      <li>Added dark mode support with improved UI theming.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Elevated exports to top level with improved termination options.</li>
      <li>Updated export mechanism for better performance and reliability.</li>
      <li>Changed header terminology from "Production" to "Wells" for clarity.</li>
      <li>Improved model loading with better local caching and remote access.</li>
      <li>Enhanced form validation logic throughout the application.</li>
      <li>Moved user sessions to user model with improved security.</li>
      <li>Updated Authorized model fetching with better error handling.</li>
      <li>Switched to v-autocomplete for improved user interface.</li>
      <li>Improved layout adjustments for smaller screens.</li>
      <li>Enhanced snackbar persistence for better user notifications.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed curve fitting errors that affected prediction accuracy.</li>
      <li>Fixed timescale switching error in forecasting module.</li>
      <li>Fixed error on login with improved authentication flow.</li>
      <li>Fixed router warnings and navigation issues.</li>
      <li>Fixed Aries type curve export functionality.</li>
      <li>Fixed incorrect date filtering in economics reports.</li>
      <li>Fixed missing calculations in forecasting outputs.</li>
      <li>Fixed stage removal from exports.</li>
      <li>Fixed infinite values in calculation outputs.</li>
      <li>Fixed issues with economics data loading and rendering.</li>
      <li>Fixed column formatting throughout the application.</li>
      <li>Fixed basin name validation and error checking.</li>
      <li>Fixed login issues and authentication flow.</li>
    </ul>

    <h2 class="text-h4" id="v5-0" v-intersect="onIntersect">v5.0</h2>
    <p>September 2025</p>
    <h3>Major Release</h3>
    <ul>
      <li>Complete Sky v5 application rebuild with all recent improvements.</li>
      <li>Repository migrated to new location with main branch architecture.</li>
      <li>Comprehensive system overhaul with modern Vue.js and Django frameworks.</li>
      <li>Enhanced performance and scalability improvements.</li>
      <li>New domain configuration system with environment-based setup.</li>
      <li>Improved Docker deployment workflow.</li>
    </ul>

    <h2 class="text-h4" id="v4-6" v-intersect="onIntersect">v4.6</h2>
    <p>February 2024</p>
    <h3>Additions</h3>
    <ul>
      <li>Added new multi-level statistics as a downloadable option to Production Forecasting.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Changed the defauilt strategy for declines when exporting predictions. Exports now default to X number of months of decline after predictions.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed an issue when re-scheduling an existing export to database.</li>
    </ul>


    <h2 class="text-h4" id="v4-5" v-intersect="onIntersect">v4.5</h2>
    <p>December 2023</p>
    <h3>Additions</h3>
    <ul>
      <li>Added new models to the system, including Anadarko, Appalachia, Barnett, Denver, Powder River, and Williston basins.</li>
      <li>Added a new ARIES economics export which exports type curves when they have been declined exponentially.</li>
      <li>Added well details when in single well mode; initially copying data from pre-loaded table, including all available filters.</li>
      <li>Added units to map title to help explain colorbar units.</li>
      <li>Added a confirmation dialog when deleting groups.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Changed how models are consumed by the system. Models now live outside the VM and are loaded from remote disks when needed.</li>
      <li>Changed how the screen switches when generating an export to reduce navigation between actions.</li>
      <li>Changed the layout of the filters in Production Forecasting to fit better on smaller screens, and to accomodate scalar units.</li>
      <li>Changed underlying structural connection bewteen exports and task result; the system now only focuses on latest result.</li>
      <li>Changed how scheduled exports are enabled; schedules are now limited to only database connected files and exports.</li>
      <li>Changed how type curves are exported; assuming some defaults in outputs to simplify the interface.</li>
      <li>Changed names of type curves when exporting for ARIES format; defaults to Prediction Name, otherwise uses Group Name.</li>
      <li>Changed which ratios are visible depending on which targets were used in the prediction.</li>
      <li>Changed how prediction exports are handled by the system internally.</li>
      <li>Changed the icon for Termination in Production Forecasting.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed an issue when the system reports an error when the export output is empty, because the user did not select any option.</li>
      <li>Fixed an issue with probit calculation when using Methods as a dimension.</li>
      <li>Fixed an issue when the map wasn't properly colored when using Lateral Length as a color feature.</li>
      <li>Fixed an issue when an edited well was not saved by the system.</li>
    </ul>


    <h2 class="text-h4" id="v4-4" v-intersect="onIntersect">v4.4</h2>
    <p>October 2023</p>
    <h3>Additions</h3>
    <ul>
      <li>Added new models (v3a) from Anadarko, Appalachia, Bernette, Denver, Powder River, and Haynesville basins.</li>
      <li>Added export table definitions in relevant locations on the help pages.</li>
      <li>Added relevant units to Oil, Gas, and Water production totals on the table headers.</li>
      <li>Added ability to export type curves to ARIES format; only support one target and method at the moment.</li>
    </ul>
    <h3>Changes</h3>
    <ul>
      <li>Updated the generic error message format to just the plain error message.</li>
      <li>Improved open dialog visuals by adding type icons and clearly identifying shared or owned items.</li>
      <li>Removed any checks and conditionals in the backend API for staff and admin users.</li>
      <li>Removed permissions on user model that limited access to only the signed in user.</li>
      <li>Changed the default behaviour of scroll zoom on the map to allow mouse scroll to work properly.</li>
      <li>Changed how scalar values are selected; replaced range sliders with numeric text fields.</li>
      <li>Changed how applications respond to user interactions when single well mode; exiting single-well mode as early as possible.</li>
      <li>Changed how many data columns are loaded by default; all filters are now automatically included in the table.</li>
      <li>Changed when table header is populated when loading.</li>
      <li>Changed the defulat visibility of mode bar on the map to be visible only when hovering.</li>
      <li>Changed the transparency of unselected wells to improve visibility of selected wells.</li>
      <li>Changed how dialog boxes are closed due to change in underlying mechanism.</li>
    </ul>
    <h3>Fixes</h3>
    <ul>
      <li>Fixed an issue where applications would not open shared predictions.</li>
      <li>Fixed an issue when loading predictions without filters in applications.</li>
      <li>Fixed an issue where units for scalar variables were set incorrectly.</li>
      <li>Fixed an issue where the first call to API resulted in a 403 error; this is expected behaviour and error is hidden.</li>
    </ul>


    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpReleaseNotes",
  components: { Contents },
  data() {
    return {
      selected: "v5-3",
      menu: [
        { id: "v5-3", text: "v5.3", depth: 1 },
        { id: "v5-2", text: "v5.2", depth: 1 },
        { id: "v5-1", text: "v5.1", depth: 1 },
        { id: "v5-0", text: "v5.0", depth: 1 },
        { id: "v4-6", text: "v4.6", depth: 1 },
        { id: "v4-5", text: "v4.5", depth: 1 },
        { id: "v4-4", text: "v4.4", depth: 1 },
      ],
    }
  },
  methods: {
    onIntersect(entries, observer) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          let target = entry.target.id
          this.$refs.contents.selected = target
        }
      }
    },
  },
}
</script>
